import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, BadgeCheck, DollarSign, Store, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminAgencyLinks } from "@/components/AdminAgencyLinks";
import { AdminAdvertising } from "@/components/AdminAdvertising";

const STATUSES = ["unverified", "pending", "verified_business", "verified_outfitter", "verified_guide", "verified_retailer", "suspended"];
const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

function Stat({ label, value }) {
  return <div className="bg-white border border-saddle/20 p-4" data-testid="admin-stat"><span className="hr-overline text-saddle">{label}</span><p className="font-serif text-2xl text-forest mt-1">{value}</p></div>;
}

export default function AdminMarketplace() {
  const navigate = useNavigate();
  const { user, isAuthed } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPlans, setSavingPlans] = useState(false);
  const isAdmin = isAuthed && (user?.roles || []).includes("admin");

  const load = useCallback(() => {
    api.marketplaceAdminOverview().then(setData).catch(() => {}).finally(() => setLoading(false));
    api.marketplacePlans().then(setPlans).catch(() => {});
  }, []);
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);
  const setVerification = async (bizId, status) => {
    await api.verifyBusiness(bizId, status).catch(() => toast.error("Failed"));
    toast.success("Verification updated");
    load();
  };
  const savePlans = async () => {
    setSavingPlans(true);
    try { await api.updateMarketplaceConfig(plans); toast.success("Plan pricing saved"); }
    catch { toast.error("Could not save"); }
    finally { setSavingPlans(false); }
  };
  const editPlan = (id, field, val) => setPlans({ ...plans, [id]: { ...plans[id], [field]: field === "fee_pct" ? Number(val) : Math.round(Number(val) * 100) } });

  if (!isAdmin) return <div className="min-h-screen bg-paper grid place-items-center px-6"><div className="text-center"><h1 className="font-serif text-2xl text-forest">Admins only</h1><button onClick={() => navigate("/")} className="mt-4 bg-forest text-bone px-5 py-2.5 text-sm font-semibold">Home</button></div></div>;
  if (loading || !data) return <div className="min-h-screen bg-paper grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-marketplace-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="admin-back"><ArrowLeft size={15} /> Back to HomeRange</button>
        <span className="hr-overline text-saddle">Admin</span>
        <h1 className="font-serif text-4xl text-forest mt-2">Marketplace control</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6" data-testid="admin-stats">
          <Stat label="Businesses" value={data.businesses} />
          <Stat label="Active subs" value={data.active_subscriptions} />
          <Stat label="Pending verify" value={data.pending_verifications} />
          <Stat label="Transactions" value={data.transactions} />
          <Stat label="Fees collected" value={dollars(data.fees_collected_cents)} />
          <Stat label="Pending payout" value={dollars(data.pending_payout_cents)} />
        </div>

        {/* Plan pricing editor */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-forest mb-3 flex items-center gap-2"><DollarSign size={20} /> Plan pricing & fees</h2>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="plan-editor">
            {["business", "pro", "elite"].map((id) => plans[id] && (
              <div key={id} className="bg-white border border-saddle/20 p-4" data-testid={`plan-edit-${id}`}>
                <h3 className="font-serif text-lg text-forest">{plans[id].name}</h3>
                <label className="block text-xs text-charcoal/60 mt-3">Monthly price ($)
                  <input type="number" defaultValue={(plans[id].price_cents / 100).toFixed(0)} onChange={(e) => editPlan(id, "price_cents", e.target.value)} className="w-full border border-saddle/25 px-2 py-1.5 mt-1 outline-none focus:border-forest" data-testid={`price-${id}`} />
                </label>
                <label className="block text-xs text-charcoal/60 mt-2">Marketplace fee (%)
                  <input type="number" step="0.1" defaultValue={plans[id].fee_pct} onChange={(e) => editPlan(id, "fee_pct", e.target.value)} className="w-full border border-saddle/25 px-2 py-1.5 mt-1 outline-none focus:border-forest" data-testid={`fee-${id}`} />
                </label>
              </div>
            ))}
          </div>
          <button onClick={savePlans} disabled={savingPlans} className="mt-3 bg-forest text-bone px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="save-plans">
            {savingPlans ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save pricing
          </button>
        </div>

        {/* Businesses + verification */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-forest mb-3 flex items-center gap-2"><Store size={20} /> Businesses</h2>
          <div className="space-y-2" data-testid="admin-business-list">
            {data.business_list.map((b) => (
              <div key={b.id} className="flex items-center gap-3 bg-white border border-saddle/20 px-4 py-3 flex-wrap" data-testid="admin-business-row">
                <div className="flex-1 min-w-0">
                  <strong className="text-forest">{b.name}</strong>
                  <span className="block text-xs text-charcoal/50">{b.category} · {b.plan || "no plan"} · {b.subscription_status}</span>
                </div>
                {b.verification_status?.startsWith("verified") && <BadgeCheck size={16} className="text-[#2f7d4f]" />}
                <select value={b.verification_status} onChange={(e) => setVerification(b.id, e.target.value)} className="border border-saddle/25 px-2 py-1.5 text-sm outline-none" data-testid="verify-select">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Agency links: partner + health */}
        <AdminAgencyLinks />

        {/* Advertising marketplace management */}
        <AdminAdvertising />

        {/* Recent transactions */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-forest mb-3">Recent transactions</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-transactions">
              <thead><tr className="border-b border-saddle/15 text-left text-charcoal/50"><th className="p-2">Gross</th><th className="p-2">Fee %</th><th className="p-2">Fee</th><th className="p-2">Payout</th><th className="p-2">Status</th><th className="p-2">Payout status</th></tr></thead>
              <tbody>
                {data.recent_transactions.map((t) => (
                  <tr key={t.id} className="border-b border-saddle/10" data-testid="admin-txn-row">
                    <td className="p-2 tabular-nums">{dollars(t.gross_cents)}</td><td className="p-2">{t.fee_pct}%</td>
                    <td className="p-2 tabular-nums">{dollars(t.fee_cents)}</td><td className="p-2 tabular-nums text-[#2f7d4f]">{dollars(t.payout_cents)}</td>
                    <td className="p-2">{t.order_status}</td><td className="p-2">{t.payout_status}</td>
                  </tr>
                ))}
                {data.recent_transactions.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-charcoal/50">No transactions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
